import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminAiUsers, adminSetWindow, adminGrant } from '../lib/practiceApi';

const BG = '#0a0a0b';
const PANEL = '#100f0d';
const BORDER = '#2a2620';
const TEXT = '#e9e5dc';
const MUTE = '#8b857a';
const A = '#C9A84C';
const MONO = "'JetBrains Mono','SF Mono',Menlo,Consolas,monospace";
const APPS = ['trapworld', 'barcode'];

const fmt = (s) => (s == null ? '∞' : `${Math.floor(s / 60)}:${String(Math.round(s) % 60).padStart(2, '0')}`);

const label = { fontFamily: MONO, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: MUTE };
const panel = { background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 4 };
const btn = { fontFamily: MONO, fontSize: 11, letterSpacing: '0.08em', color: A, background: 'transparent', border: `1px solid ${A}66`, borderRadius: 3, padding: '5px 9px', cursor: 'pointer' };
const inp = { fontFamily: MONO, fontSize: 13, color: TEXT, background: '#0c0b09', border: `1px solid ${BORDER}`, borderRadius: 3, padding: '8px 10px' };

export default function AdminPage() {
  const { token } = useAuth();
  const [app, setApp] = useState('trapworld');
  const [data, setData] = useState(null);     // { window_seconds, users }
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [winInput, setWinInput] = useState('');
  const [grantEmail, setGrantEmail] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const d = await adminAiUsers(token, app);
      setData(d); setDenied(false);
      setWinInput(String(Math.round((d.window_seconds || 0) / 60)));
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
    } finally { setBusy(false); }
  }, [token, app]);

  useEffect(() => { load(); }, [load]);

  const saveWindow = async () => {
    const mins = parseInt(winInput, 10);
    if (!Number.isFinite(mins) || mins < 0) return;
    await adminSetWindow(token, app, mins * 60); load();
  };
  const grant = async (payload) => { await adminGrant(token, { app, ...payload }); load(); };

  if (denied) {
    return (
      <div style={{ background: BG, minHeight: '100vh', color: TEXT, fontFamily: MONO, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ ...panel, padding: 28, textAlign: 'center' }}>
          <div style={{ ...label, color: '#ff4d6d' }}>Not authorized</div>
          <div style={{ color: MUTE, fontSize: 13, marginTop: 8 }}>This control room is owner-only.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: BG, minHeight: '100vh', color: TEXT, fontFamily: MONO }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '28px 20px 64px' }}>
        <h1 style={{ fontFamily: MONO, fontSize: 26, letterSpacing: '0.14em', color: A, margin: '0 0 4px' }}>CUTOFF CONTROL</h1>
        <div style={{ ...label, marginBottom: 20 }}>AI demo meter · owner only</div>

        {/* App tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          {APPS.map((a) => (
            <button key={a} onClick={() => setApp(a)}
              style={{ ...btn, color: app === a ? '#000' : A, background: app === a ? A : 'transparent', fontWeight: app === a ? 700 : 400, textTransform: 'uppercase' }}>
              {a}
            </button>
          ))}
          <button onClick={load} style={{ ...btn, marginLeft: 'auto', color: MUTE, borderColor: BORDER }}>{busy ? '…' : '↻ Refresh'}</button>
        </div>

        {/* Window dial */}
        <section style={{ ...panel, padding: 18, marginBottom: 16 }}>
          <div style={{ ...label, color: A, marginBottom: 10 }}>Demo window for “{app}”</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input value={winInput} onChange={(e) => setWinInput(e.target.value)} style={{ ...inp, width: 90 }} />
            <span style={{ color: MUTE, fontSize: 13 }}>minutes of AI per user</span>
            <button onClick={saveWindow} style={{ ...btn, color: '#000', background: A, fontWeight: 700 }}>Save</button>
          </div>
          <div style={{ color: MUTE, fontSize: 11, marginTop: 8 }}>
            Currently {Math.round((data?.window_seconds || 0) / 60)} min. Changing this affects everyone, including users already mid-window.
          </div>
        </section>

        {/* Grant by email */}
        <section style={{ ...panel, padding: 18, marginBottom: 16 }}>
          <div style={{ ...label, color: A, marginBottom: 10 }}>Grant access by email</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="artist@email.com" style={{ ...inp, flex: '1 1 220px' }} />
            <button onClick={() => grantEmail && grant({ email: grantEmail, op: 'unlock' })} style={btn}>Unlock</button>
            <button onClick={() => grantEmail && grant({ email: grantEmail, op: 'reset' })} style={btn}>Reset</button>
            <button onClick={() => grantEmail && grant({ email: grantEmail, op: 'add_minutes', minutes: 20 })} style={btn}>+20 min</button>
          </div>
        </section>

        {/* User list */}
        <section style={{ ...panel, padding: 4 }}>
          <div style={{ ...label, color: A, padding: '12px 14px 8px' }}>Users on “{app}” ({data?.users?.length || 0})</div>
          {(!data?.users || data.users.length === 0) && (
            <div style={{ color: MUTE, fontSize: 13, padding: '4px 14px 16px' }}>No one has touched the AI yet.</div>
          )}
          {data?.users?.map((u) => (
            <div key={u.user_id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 14px', borderTop: `1px solid ${BORDER}`, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                <div style={{ fontSize: 13, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email || u.username || u.user_id}</div>
                <div style={{ fontSize: 11, color: MUTE }}>
                  {u.unlocked ? '✦ unlocked' : u.started_at ? `${fmt(u.remaining_seconds)} left` : 'not started'}
                </div>
              </div>
              <button onClick={() => grant({ user_id: u.user_id, op: u.unlocked ? 'lock' : 'unlock' })} style={btn}>{u.unlocked ? 'Lock' : 'Unlock'}</button>
              <button onClick={() => grant({ user_id: u.user_id, op: 'reset' })} style={{ ...btn, color: MUTE, borderColor: BORDER }}>Reset</button>
              <button onClick={() => grant({ user_id: u.user_id, op: 'add_minutes', minutes: 5 })} style={{ ...btn, color: MUTE, borderColor: BORDER }}>+5</button>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
